import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import {
  GetLtiIntegrationResources,
  InitiateLtiLaunch,
} from "../../../services/LtiIntegrationService";
import { handleTableScroll } from "../../../utils/commonFunction";
import { TABLE_ROWS_PER_PAGE } from "../../../utils/Constants";
import Str from "../../common/Str";

/**
 * Starts authenticated LTI launch and navigates browser to Tool login URL.
 * Direct /lti/launch links cannot send JWT Authorization headers.
 */
async function startAuthenticatedResourceLaunch(integrationId, resourceUrl) {
  const response = await InitiateLtiLaunch({
    integration_id: integrationId,
    target_link_uri: resourceUrl,
  });
  const redirectUrl = response?.data?.redirectUrl;
  if (!redirectUrl) {
    throw new Error("LTI launch redirect URL was not returned.");
  }
  window.open(redirectUrl, "_blank");
}

/**
 * Resources tab for one LTI integration.
 */
function IntegrationResources({ integrationId }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [launchingUrl, setLaunchingUrl] = useState("");

  useEffect(() => {
    handleTableScroll();
  }, [loading]);

  /**
   * Loads resource list.
   */
  const fetchResources = async (cancelToken) => {
    try {
      setLoading(true);
      const response = await GetLtiIntegrationResources(integrationId, cancelToken);
      setResources(response?.data?.resources || []);
    } catch (error) {
      if (!axios.isCancel(error)) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something Went Wrong!.",
        });
        setResources([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (integrationId) {
      fetchResources(source.token);
    }

    return () => source.cancel("Component unmounted");
  }, [integrationId]);

  const filteredResources = useMemo(() => {
    if (!search.trim()) {
      return resources;
    }

    const value = search.trim().toLowerCase();
    return resources.filter((item) => {
      return (
        (item.name || "").toLowerCase().includes(value) ||
        (item.url || "").toLowerCase().includes(value)
      );
    });
  }, [resources, search]);

  /**
   * Clears table search filter.
   */
  const resetFilter = () => {
    setSearch("");
  };

  /**
   * Updates search filter value.
   */
  const handleSearchFilter = (event) => {
    setSearch(event.target.value);
  };

  /**
   * Launches a resource via authenticated Platform → Tool flow.
   */
  const handleLaunchResource = async (resourceUrl) => {
    if (!integrationId || !resourceUrl || launchingUrl === resourceUrl) {
      return;
    }

    try {
      setLaunchingUrl(resourceUrl);
      await startAuthenticatedResourceLaunch(integrationId, resourceUrl);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Launch failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start LTI launch.",
      });
    } finally {
      setLaunchingUrl("");
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: "name",
        sortable: true,
        cell: (row) => (
          <span className="feature-name" title={row.name}>
            <span className="textLimit100">{row.name}</span>
          </span>
        ),
      },
      {
        name: "Image",
        selector: "image",
        cell: (row) =>
          row.image ? (
            <img src={row.image} alt={row.name} width="60" />
          ) : (
            "-"
          ),
      },
      {
        name: "URL",
        selector: "url",
        sortable: true,
        cell: (row) =>
          row.url ? (
            <Link
              className="as-text-blue curser feature-name"
              to="#"
              target="_blank"
              onClick={(event) => {
                event.preventDefault();
                handleLaunchResource(row.url);
              }}
              title={`Launch: ${row.url}`}
            >
              <span className="textLimit100">{row.url}</span>
            </Link>
          ) : (
            "-"
          ),
      },
      {
        name: "Action",
        cell: (row) =>
          row.url ? (
            <div className="assessment-08">
              <div className="as-buttons">
                <button
                  type="button"
                  className="btn btn-primary rounded-circle"
                  onClick={() => handleLaunchResource(row.url)}
                  disabled={launchingUrl === row.url}
                  title="Launch"
                >
                  {launchingUrl === row.url ? (
                    <i className="fas fa-cog fa-spin"></i>
                  ) : (
                    <i className="fal fa-external-link"></i>
                  )}
                </button>
              </div>
            </div>
          ) : (
            "-"
          ),
      },
    ],
    [integrationId, launchingUrl]
  );

  if (!integrationId) {
    return (
      <div className="card card-body-inr">
        <p className="mb-0">Please open an integration first.</p>
      </div>
    );
  }

  return (
    <div className="my-tickets-info-list Tickets-main-wrap">
      <div className="custom-table-div filter-search-icon card card-table-custom">
        <div className="search-filter-div">
          <div className="search-filter-div-left">
            <div className="system-administration-table table-responsive">
              <div className="table-responsive-div">
                <div id="assessment-table-main_wrapper" className="dataTables_wrapper no-footer">
                  <div id="assessment-table-main_filter" className="dataTables_filter">
                    <label>
                      <input
                        type="search"
                        placeholder="Search"
                        aria-controls="assessment-table-main"
                        onChange={handleSearchFilter}
                        value={search}
                      />
                    </label>
                    <div className="filter-eff filter-data-btn">
                      <button className="filter-buttons" type="button">
                        <i className="fal fa-filter"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="filter-button-group">
                <div className="reset-btn-group">
                  <div className="button-reset dropdown-comman">
                    <button className="btn btn-primary" onClick={resetFilter} title="Reset" type="button">
                      <i className="fal fa-redo"></i>Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonTicketList />
        ) : (
          <DataTable
            data={filteredResources}
            columns={columns}
            defaultSortField="name"
            defaultSortAsc={true}
            pagination={true}
            noDataComponent={Str.noRecord}
            paginationRowsPerPageOptions={TABLE_ROWS_PER_PAGE}
          />
        )}
      </div>
    </div>
  );
}

export default IntegrationResources;
